// New screen (no old counterpart): the pending-approval list and the approve / reject dialog of the
// second-person approval of D-035 / D-119 (C18 routes /approvals*). The old application had no approval
// workflow (every operation posted at once), so this dialog is composed from the old app's own modal
// vocabulary — the history-modal frame (App.tsx:50268-50300: backdrop, rounded-3xl card, header band,
// close button), the users-card row (App.tsx:14803-14942 classes) and the confirmation block of the
// edit-user dialog (App.tsx:57775-57789) — so it reads as part of the same product (D-003 "feel").
// Figures arrive formatted by the container; the PIN is asked by the container's PIN gate (D-018).
import { useState } from 'react';
import { motion } from 'motion/react';
import { CheckCircle, Clock, ShieldCheck, X, XCircle } from 'lucide-react';
import { useI18n } from '../i18n-context.js';

export interface ApprovalRow {
  readonly id: string;
  /** Operation type label (container: `optype.<type>`). */
  readonly typeLabel: string;
  /** IQD-equivalent amount, formatted. */
  readonly amountLabel: string;
  readonly maker: string;
  readonly createdLabel: string;
  readonly status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  readonly statusLabel: string;
  /** Short description of the input (container), e.g. "من الخزنة إلى زين كاش". */
  readonly description: string;
  readonly decidedBy?: string | null;
  readonly reason?: string | null;
  /** The viewer may decide this row (approver ≠ maker, holds `approve_operations`). */
  readonly canDecide: boolean;
  /** The viewer is the maker and may cancel it. */
  readonly canCancel: boolean;
}

export interface ApprovalsPanelProps {
  readonly rows: readonly ApprovalRow[];
  readonly loading?: boolean;
  readonly onApprove: (id: string) => void;
  readonly onReject: (id: string, reason: string) => void;
  readonly onCancel: (id: string) => void;
  readonly onClose: () => void;
  readonly busyId?: string | null;
}

const STATUS_CLASS: Record<ApprovalRow['status'], string> = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/20',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-500/20',
  rejected: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:border-rose-500/20',
  cancelled: 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700',
};

export function ApprovalsPanel(props: ApprovalsPanelProps) {
  const { t } = useI18n();
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="approvals-panel">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={props.onClose} className="absolute inset-0 bg-black/60 backdrop-blur-md" />
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        className="relative bg-white dark:bg-[#0d1117] w-full max-w-4xl rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] border border-gray-100 dark:border-[#30363d] dark:digital-border"
      >
        <div className="p-6 border-b border-gray-100 dark:border-[#30363d] flex justify-between items-center bg-gray-50/50 dark:bg-[#05070a]">
          <div>
            <h3 className="text-xl font-black text-gray-900 dark:text-[#f0f6fc] flex items-center gap-2 uppercase tracking-tight dark:digital-glow">
              <ShieldCheck className="text-amber-600 dark:text-amber-400" />
              {t('approvals.title')}
            </h3>
            <p className="text-sm text-gray-500 dark:text-[#8b949e] mt-1 font-bold">{t('approvals.subtitle')}</p>
          </div>
          <button onClick={props.onClose} className="p-2 hover:bg-gray-200 dark:hover:bg-[#161b22] rounded-full transition-colors text-gray-400 dark:text-[#8b949e] hover:text-gray-600 dark:hover:text-[#f0f6fc]" data-testid="approvals-close">
            <X size={24} />
          </button>
        </div>
        <div className="p-6 overflow-y-auto custom-scrollbar flex-1 space-y-3" data-testid="approvals-list">
          {props.loading ? (
            <p className="text-center text-sm font-bold text-gray-400 dark:text-[#8b949e] py-10">{t('history.loading')}</p>
          ) : props.rows.length === 0 ? (
            <p className="text-center text-sm font-bold text-gray-400 dark:text-[#8b949e] py-10" data-testid="approvals-empty">{t('approvals.empty')}</p>
          ) : (
            props.rows.map((r) => (
              <div key={r.id} data-testid={`approval-row-${r.id}`} className="p-4 bg-gray-50 dark:bg-[#05070a] rounded-xl border border-gray-100 dark:border-[#30363d] dark:digital-border space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-black text-sm dark:text-[#f0f6fc] uppercase tracking-tight dark:digital-glow flex items-center gap-2">
                      <span>{r.typeLabel}</span>
                      <span className="font-mono text-primary-600 dark:text-primary-400">{r.amountLabel}</span>
                    </p>
                    <p className="text-xs text-gray-500 dark:text-[#8b949e] font-bold mt-1">{r.description}</p>
                    <p className="text-[10px] text-gray-400 dark:text-[#8b949e] font-bold mt-1 flex items-center gap-1">
                      <Clock size={12} />
                      {t('approvals.by_at', { user: r.maker, at: r.createdLabel })}
                      {r.decidedBy ? ` · ${t('approvals.decided_by', { user: r.decidedBy })}` : ''}
                      {r.reason ? ` · ${t('approvals.reason')}: ${r.reason}` : ''}
                    </p>
                  </div>
                  <span className={`px-2.5 py-1 rounded-full text-[10px] font-black border uppercase tracking-wider ${STATUS_CLASS[r.status]}`}>{r.statusLabel}</span>
                </div>
                {r.status === 'pending' && (r.canDecide || r.canCancel) && (
                  <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-100 dark:border-[#30363d]">
                    {r.canDecide && rejecting !== r.id && (
                      <>
                        <button
                          type="button"
                          disabled={props.busyId === r.id}
                          onClick={() => props.onApprove(r.id)}
                          data-testid={`approve-${r.id}`}
                          className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white py-2.5 rounded-xl text-[10px] font-black flex items-center justify-center gap-2 shadow-lg shadow-emerald-600/20 uppercase tracking-widest transition-all active:scale-95 disabled:opacity-50"
                        >
                          <CheckCircle size={14} />
                          {t('approvals.approve')}
                        </button>
                        <button
                          type="button"
                          disabled={props.busyId === r.id}
                          onClick={() => {
                            setRejecting(r.id);
                            setReason('');
                          }}
                          data-testid={`reject-${r.id}`}
                          className="flex-1 bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400 py-2.5 rounded-xl text-[10px] font-black flex items-center justify-center gap-2 border border-rose-100 dark:border-rose-500/20 uppercase tracking-widest transition-all active:scale-95 disabled:opacity-50"
                        >
                          <XCircle size={14} />
                          {t('approvals.reject')}
                        </button>
                      </>
                    )}
                    {r.canDecide && rejecting === r.id && (
                      <div className="w-full space-y-2" data-testid={`reject-form-${r.id}`}>
                        <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] uppercase tracking-widest text-right">{t('approvals.reject_reason')}</label>
                        <input
                          type="text"
                          value={reason}
                          onChange={(e) => setReason(e.target.value)}
                          data-testid={`reject-reason-${r.id}`}
                          className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:text-[#f0f6fc] font-black transition-all"
                          placeholder={t('approvals.reject_reason_placeholder')}
                        />
                        <div className="flex gap-2">
                          <button
                            type="button"
                            disabled={!reason.trim() || props.busyId === r.id}
                            onClick={() => {
                              props.onReject(r.id, reason.trim());
                              setRejecting(null);
                            }}
                            data-testid={`reject-confirm-${r.id}`}
                            className="flex-1 bg-rose-600 hover:bg-rose-500 text-white py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all active:scale-95 disabled:opacity-50"
                          >
                            {t('approvals.reject_confirm')}
                          </button>
                          <button type="button" onClick={() => setRejecting(null)} className="flex-1 bg-gray-100 dark:bg-[#161b22] text-gray-600 dark:text-[#8b949e] py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all">
                            {t('cancel')}
                          </button>
                        </div>
                      </div>
                    )}
                    {r.canCancel && (
                      <button
                        type="button"
                        disabled={props.busyId === r.id}
                        onClick={() => props.onCancel(r.id)}
                        data-testid={`cancel-${r.id}`}
                        className="flex-1 bg-gray-100 dark:bg-[#161b22] text-gray-600 dark:text-[#8b949e] py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all active:scale-95 disabled:opacity-50"
                      >
                        {t('approvals.cancel_request')}
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </motion.div>
    </div>
  );
}
